/**
 * Illustrative Support at Home service plan for the suggestion engine when the
 * user has not built a Service Plan in Service Builder.
 *
 * Purpose: Strategy comparison needs a consistent dollar cost of care so
 * contribution rates translate into contribution $, cashflow, and legacy impacts.
 * This is NOT a substitute for a formal assessment or a tailored service plan.
 */

/** One line item within a Service Builder plan side (client/partner): quantity of a service at a frequency. */
export interface ServicePlanTaskAllocation {
    quantity: number;
    frequency: 'daily' | 'weekly' | 'fortnightly' | 'monthly' | string;
}

/** { [serviceId]: allocation } for one care recipient. */
export type SupportAtHomeServicePlanSide = Record<string, ServicePlanTaskAllocation | undefined>;

export interface SupportAtHomeServicePlan {
    client?: SupportAtHomeServicePlanSide;
    partner?: SupportAtHomeServicePlanSide;
}

export interface ResolvedServicePlan {
    plan: { client: SupportAtHomeServicePlanSide; partner: SupportAtHomeServicePlanSide };
    isEstimated: boolean;
    description: string | null;
}

/** Moderate illustrative package (single client) — documented in assumptions UI. */
export const ILLUSTRATIVE_SAH_SERVICE_PLAN_CLIENT: SupportAtHomeServicePlanSide = {
    nursing_registered: { quantity: 1, frequency: 'monthly' },
    personal_care: { quantity: 6, frequency: 'weekly' },
    domestic_assistance: { quantity: 3, frequency: 'weekly' },
    social_support_community_engagement: { quantity: 2, frequency: 'weekly' },
    transport: { quantity: 4, frequency: 'monthly' },
    meal_delivery: { quantity: 8, frequency: 'monthly' },
};

export const ILLUSTRATIVE_SAH_SERVICE_PLAN_DESCRIPTION =
    'Illustrative moderate package (not your actual plan): ~6 hrs personal care/week, ~3 hrs domestic/week, ~2 hrs social support/week, ~4 transport trips/month, ~8 meals/month, ~1 hr nursing/month. Dollar costs use median catalogue prices; your contribution $ uses your modelled contribution rates.';

export function hasUsableServicePlan(servicePlan: SupportAtHomeServicePlan | null | undefined): boolean {
    if (!servicePlan) return false;
    const client = servicePlan.client || {};
    const partner = servicePlan.partner || {};
    const hasClient = Object.values(client).some((d) => d && d.quantity > 0);
    const hasPartner = Object.values(partner).some((d) => d && d.quantity > 0);
    return hasClient || hasPartner;
}

export function resolveServicePlanForSuggestionEngine(
    servicePlan: SupportAtHomeServicePlan | null | undefined,
    isPartnered = false,
): ResolvedServicePlan {
    if (hasUsableServicePlan(servicePlan)) {
        return {
            plan: {
                client: servicePlan?.client || {},
                partner: isPartnered ? (servicePlan?.partner || {}) : {},
            },
            isEstimated: false,
            description: null,
        };
    }

    /*
     * No services chosen: nothing to price (owner's decision 2, 19 Oct 2026, enforced 20 Oct 2026 — the
     * engine's baseline charged the illustrative package below while step 1 priced the empty plan, so
     * "each year" read short by $29,071 against Home's $1,058 to spare). The package is kept only as a
     * named example; it is no longer substituted for an empty plan anywhere.
     */
    return { plan: { client: {}, partner: {} }, isEstimated: false, description: null };
}
